import { z } from 'zod';

import authService from './auth/auth';
import { apiRoutes } from './config';

const statusSchema = z.union([
  z.literal('FAILED'),
  z.literal('PROCESSING'),
  z.literal('READY'),
]);

export const recruiterChoiceSchema = z.enum(['MIKE', 'CHRIS']);
export type RecruiterChoice = z.infer<typeof recruiterChoiceSchema>;

export type BuyResumeChecking =
  | {
      publicId?: string;
      cvFile: File;
      recruiterChoice: RecruiterChoice;
    }
  | {
      publicId: string;
      cvFile?: File;
      recruiterChoice: RecruiterChoice;
    };

const resumeAnalyzerDetailSchema = z.object({
  name: z.string(),
  rate: z.number(),
  explanation: z.string(),
  tips: z.array(z.string()).optional(),
  weight: z.number().optional(),
  sectionName: z.string().optional(),
  sectionKey: z.string().nullable().optional(),
  fieldKey: z.string().nullable().optional(),
  missingInfoLabel: z.string().nullable().optional(),
});

export type ResumeAnalyzerDetail = z.infer<typeof resumeAnalyzerDetailSchema>;

export const resumeSectionSchema = z.enum([
  'WORK_EXPERIENCE',
  'SKILLS',
  'EDUCATION',
  'CERTIFICATES',
  'LANGUAGES',
  'PROJECTS',
  'EXTRA_ACTIVITIES',
  'LINKS',
  'MEDICAL_LICENSES',
  'BAR_ADMISSIONS',
  'PROFESSIONAL_CERTIFICATIONS',
]);
export type ResumeSectionKey = z.infer<typeof resumeSectionSchema>;

export const industryValues = ['TECH', 'HEALTH'] as const;
export const industrySchema = z.enum(industryValues);
export type Industry = (typeof industryValues)[number];

export const industryConfigSchema = z
  .object({
    industry: industrySchema.optional(),
    allowedSections: z.array(resumeSectionSchema),
    sectionLabelOverrides: z
      .record(resumeSectionSchema, z.string())
      .optional()
      .default({}),
  })
  .nullable();

export type IndustryConfig = z.infer<typeof industryConfigSchema>;

const aiIssueFixStatusSchema = z.object({
  status: statusSchema,
  sectionKey: z.string().nullable(),
  fieldKey: z.string().nullable(),
  startedAt: z.string().nullable(),
  result: z
    .object({
      success: z.boolean(),
      reason: z.string(),
      missingInfoLabel: z.string().nullable(),
    })
    .nullable(),
});

export type AiIssueFixStatus = z.infer<typeof aiIssueFixStatusSchema>;

export const resumeAnalyzerSchema = z.object({
  generalStatus: statusSchema,
  rate: z.object({
    overallRate: z.number(),
    contentRate: z.number(),
    styleRate: z.number(),
  }),
  industryConfig: industryConfigSchema,
  sectionsStatus: z.object({
    textExtractionStatus: statusSchema,
    textProcessingStatus: statusSchema,
    personalInfoStatus: statusSchema,
    skillsStatus: statusSchema,
    languagesStatus: statusSchema,
    educationStatus: statusSchema,
    experienceStatus: statusSchema,
    certificatesStatus: statusSchema,
    medicalLicensesStatus: statusSchema,
    projectsStatus: statusSchema,
  }),
  issues: z.object({
    urgent: z.array(resumeAnalyzerDetailSchema),
    optional: z.array(resumeAnalyzerDetailSchema),
    strength: z.array(resumeAnalyzerDetailSchema),
  }),
  aiIssueFix: aiIssueFixStatusSchema,
  resumeId: z.number().nullable(),
});

export type ResumeAnalyzer = z.infer<typeof resumeAnalyzerSchema>;

export enum ResumeRateStatus {
  URGENT = 'urgent',
  OPTIONAL = 'optional',
  STRENGTH = 'strength',
}

export const postResumeAnalyzer = async ({
  file,
  utmParams,
  industry,
}: {
  file: File | Blob;
  utmParams: Record<string, string | undefined>;
  industry: Industry;
}) => {
  const res = await authService.post(
    apiRoutes.resume.fromFile,
    { cv_file: file, utm_params: utmParams, industry },
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  if (res.status === 402) {
    return z.object({ url: z.string() }).parse(res.data);
  }
  return z.object({ publicId: z.string() }).parse(res.data);
};

export const getResumeAnalyzer = async (id: string) => {
  const { data } = await authService.get(apiRoutes.resume.detail(id));
  return resumeAnalyzerSchema.parse(data);
};

export const getResumesCredits = async () => {
  const { data } = await authService.get(apiRoutes.resume.usage);
  return z
    .object({
      credits: z.string(),
      userLeftFeedback: z.boolean(),
    })
    .parse(data);
};

export const buyResumeChecking = async ({
  publicId,
  cvFile,
  recruiterChoice,
}: BuyResumeChecking) => {
  const { data } = await authService.post(
    apiRoutes.resume.buyResumeChecking,
    {
      public_id: publicId,
      cv_file: cvFile,
      recruiter_choice: recruiterChoice,
    },
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return z.object({ url: z.string() }).parse(data);
};
